<script setup lang="ts">
import { computed } from 'vue'

// Idioma
const { locale, t } = useI18n()

// Datos Estáticos del Footer
const keywords: any = computed(() => [
  { label: t('strapi.global.footer.keywords.hotelesPorHoras'), link: 'search', url: true },
  { label: t('strapi.global.footer.keywords.hotelDeDia'), link: 'hotel-de-dia-468l', url: false },
  { label: t('strapi.global.footer.keywords.habitacionPorHoras'), link: 'habitacion-por-horas-401l', url: false }
])

const privacidadLinks: any = computed(() => [
  { label: t('strapi.global.footer.privacidadLinks.acuerdo'), link: `/${locale.value}/terms.html`, url: true, target: '_self' },
  { label: t('strapi.global.footer.privacidadLinks.privacidad'), link: `/${locale.value}/privacy.html`, url: true, target: '_self' },
])

const infoLinks: any = computed(() => [
  { label: t('strapi.global.footer.infoLinks.ayuda'), link: `https://byhours-support.gorgias.help/${locale.value}-${countryLangCodes[locale.value]}`, url: true },
  { label: t('strapi.global.footer.infoLinks.contacto'), link: `https://byhours-support.gorgias.help/${locale.value}-${countryLangCodes[locale.value]}/contacto-563592`, url: true },
  { label: t('strapi.global.footer.infoLinks.blog'), link: 'https://blog.byhours.com', url: true }
])

const suggestedDestinations: Ref<StrapiDestination[]> = useState('suggested-destinations', () => [])

const redes: any = [
  { label: 'Instagram', link: 'https://www.instagram.com/byhours', url: true, img: { url: '/uploads/instagram_eb04751b04.svg', provider: 'local' } },
  { label: 'Facebook', link: 'https://www.facebook.com/byhours', url: true, img: { url: '/uploads/facebook_1525a071f9.svg', provider: 'local' } },
  { label: 'X', link: 'https://x.com/byhours', url: true, img: { url: '/uploads/x_6beef74668.svg', provider: 'local' } },
  { label: 'Linkedin', link: 'https://www.linkedin.com/company/2358969?trk=NUS_CMPY_TWIT', url: true, img: { url: '/uploads/linkedin_609ad40bde.svg', provider: 'local' } },
  { label: 'Tik Tok', link: 'https://www.tiktok.com/@byhourstravel', url: true, img: { url: '/uploads/tiktok_f3abf2e889.svg', provider: 'local' } },
  { label: 'Youtube', link: 'https://www.youtube.com/user/ByHoursTv', url: true, img: { url: '/uploads/youtube_82e6e94e8f.svg', provider: 'local' } }
]

const buttonsApp: any = [
  { label: 'App Store', link: 'http://bit.ly/10Dxkt6', url: true, img: { url: '/uploads/appstore_b54a1d0a69.svg', provider: 'local' } },
  { label: 'Google Play', link: 'http://bit.ly/11OTuel', url: true, img: { url: '/uploads/googleplay_cebe4be873.svg', provider: 'local' } }
]

const buttonHotelero: any = computed(() => ({
  label: t('strapi.global.footer.buttonHotelero.label'),
  link: 'https://admin.byhours.com/',
  url: true
}))

const buttonAgencia: any = computed(() => ({
  label: t('strapi.global.footer.buttonAgencia.label'),
  link: 'https://agencies.byhours.com/login',
  url: true
}))

</script>
<template>
  <footer class="bg-neutral-950 text-white">
    <div :class="`mx-auto max-w-screen-2xl py-12 flex flex-col gap-6 px-5`">
      <div class="flex flex-wrap gap-8">
        <div v-for="item in keywords" :key="item.label">
          <UButton v-if="!item.url" variant="solid" color="neutral" size="xl" :label="item.label"
            :to="getButtonUrlNew(item)" />
          <UButton v-else variant="solid" color="neutral" size="xl" :label="item.label"
            :to="`/${locale}/${item.link}`" />
        </div>
      </div>
      <USeparator type="dashed" class="py-4" />
      <div class="grid gap-8 md:gap-2 grid-cols-2 md:grid-cols-4">
        <div>
          <p class="font-semibold mb-2">{{ t('strapi.global.footer.tituloPrivacidad') }}</p>
          <ul class="flex flex-col gap-1">
            <li v-for="item in privacidadLinks" :key="item.label">
              <NuxtLink :aria-label="item.label" :to="getButtonUrl(item)">
                <p class="text-neutral-400">{{ item.label }}</p>
              </NuxtLink>
            </li>
          </ul>
        </div>
        <div>
          <p class="font-semibold mb-2">{{ t('strapi.global.footer.tituloInformacion') }}</p>
          <ul class="flex flex-col gap-1">
            <li v-for="item in infoLinks" :key="item.label">
              <NuxtLink :aria-label="item.label" :to="getButtonUrl(item)" target="_blank">
                <p class="text-neutral-400">{{ item.label }}</p>
              </NuxtLink>
            </li>
          </ul>
        </div>
        <div>
          <div class="mb-8">
            <p class="font-semibold mb-2">{{ t('strapi.global.footer.tituloHoteleros') }}</p>
            <p class="text-neutral-400">{{ t('strapi.global.footer.subtituloHoteleros') }}</p>
            <UButton size="md" color="neutral" variant="outline" :aria-label="buttonHotelero.label" class="my-2"
              :to="getButtonUrl(buttonHotelero)" target="_blank">{{
                buttonHotelero.label }}</UButton>
          </div>
          <div>
            <p class="font-semibold mb-2">{{ t('strapi.global.footer.tituloAgencia') }}</p>
            <p class="text-neutral-400">{{ t('strapi.global.footer.subtituloAgencia') }}</p>
            <UButton size="md" color="neutral" variant="outline" :aria-label="buttonAgencia.label" class="my-2"
              :to="getButtonUrl(buttonAgencia)" target="_blank">{{
                buttonAgencia.label }}</UButton>
          </div>
        </div>
        <div>
          <p class="font-semibold mb-2">{{ t('strapi.global.footer.tituloDestinos') }}</p>
          <ul class="grid grid-cols-2 gap-x-4 gap-y-1 w-full text-neutral-400">
            <li v-for="item in suggestedDestinations.slice(0, 18)" :key="item.name" class="truncate">
              <NuxtLink :aria-label="item.name"
                :to="`/${locale}/${item.slug || slugify(item.name || 'destination')}-${item.identifier}d.html`"
                target="_self">
                {{ item.name }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
      <USeparator type="dashed" class="py-4" />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0">
        <div>
          <p class="font-semibold mb-4">{{ t('strapi.global.footer.tituloSiguenos') }}</p>
          <ul class="flex gap-6 items-center">
            <li v-for="item in redes" :key="item.label">
              <NuxtLink :aria-label="item.label" :to="getButtonUrl(item)" target="_blank">
                <NuxtImg :provider="item?.img?.provider" :src="getImageSrc(item?.img, '/home/redes')"
                  :aria-label="item.label" width="25" height="25" class="invert" loading="lazy" />
              </NuxtLink>
            </li>
          </ul>
        </div>
        <div>
          <p class="font-semibold mb-4">{{ t('strapi.global.footer.tituloApp') }}</p>
          <ul class="flex gap-6">
            <li v-for="item in buttonsApp" :key="item.label">
              <NuxtLink :aria-label="item.label" :to="getButtonUrl(item)" target="_blank">
                <NuxtImg :provider="item?.img?.provider" :src="getImageSrc(item?.img, '/home')" :aria-label="item.label"
                  width="100" height="60" class="invert" loading="lazy" />
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
</template>